/**
 * Minyanim Roll engine
 * -------------------------------------------------------------------------
 * Pure, framework-agnostic implementation of the "Minyanim Roll" spec:
 *
 *   1. Roll Types (Scheduling Logic) — decide which rolls are on screen for
 *      the current clock tick ({@link isRollActiveNow}), and which of those
 *      are LIVE — their schedule in force, so the next-minyan highlight, the
 *      {countdown}, the upcoming-minyan card and the smart-display window run
 *      ({@link isRollLiveNow}). An "Always" roll is on screen all week but
 *      live only inside its Strict window: the weekday schedule over Shabbos
 *      is a schedule on the wall, not a running one.
 *   2. Priority Logic (Overwriting)  — "Greatest Wins": when two LIVE rolls
 *      overlap, only the roll(s) with the highest priority stay on screen;
 *      an outranked roll goes off the air whole, never minyan by minyan.
 *   3. Display Toggles               — Bruches (+5 "Start Shacharit") and
 *      Hoidy (+15 "Hodu") secondary timestamps per minyan.
 *
 * The functions here take plain data + injected helpers (e.g. how to resolve
 * a minyan's time to minutes) so they can run identically on the live display,
 * in the editor preview, or in tests.
 */

import type { HolidaySuppression } from '@/utils/holiday_schedule';
import { clampColumnCount, getCenteredCardPlacement, getCenteredCardTemplateColumns } from '@/utils/column_layout';

/** Canonical roll types. Legacy values (`weekday`, `shabbos`) map to the
 *  "Always" variants for backward compatibility. */
export enum RollType {
  WeekdayAlways = 'weekday',
  WeekdayStrict = 'weekday_strict',
  WeekdayCustom = 'weekday_custom',
  ShabbosAlways = 'shabbos',
  ShabbosStrict = 'shabbos_strict',
  RoshChodesh = 'rosh_chodesh',
  YomTov = 'yom_tov',
  Custom = 'custom',
}

/** Options shown in the roll editor, in spec order (1-5 + extras). */
export const ROLL_TYPE_OPTIONS: Array<{ value: RollType; label: string; defaultPriority: number }> = [
  { value: RollType.WeekdayAlways, label: 'Weekday (Always)', defaultPriority: 1 },
  { value: RollType.WeekdayStrict, label: 'Weekday (Strict)', defaultPriority: 2 },
  { value: RollType.WeekdayCustom, label: 'Weekday (Custom)', defaultPriority: 2 },
  { value: RollType.ShabbosAlways, label: 'Shabbos (Always)', defaultPriority: 3 },
  { value: RollType.ShabbosStrict, label: 'Shabbos (Strict)', defaultPriority: 4 },
  { value: RollType.RoshChodesh, label: 'Rosh Chodesh', defaultPriority: 5 },
];

/**
 * The roll types that carry the WEEKDAY schedule — the only ones a holiday
 * ticked in Settings → Holidays can take off the air, and each only while its
 * own roll-type tick there is on (see {@link isRollHiddenByHoliday}). Shabbos,
 * Rosh Chodesh and Yom Tov rolls are never touched by it.
 */
export const WEEKDAY_ROLL_TYPES: RollType[] = [
  RollType.WeekdayAlways,
  RollType.WeekdayStrict,
  RollType.WeekdayCustom,
];

/** True for any of the three Weekday roll types (Always / Strict / Custom). */
export function isWeekdayRollType(type: unknown): boolean {
  return WEEKDAY_ROLL_TYPES.includes(normalizeRollType(type));
}

/**
 * Whether the holiday currently in effect (Settings → Holidays) takes a roll
 * of this type off the air. The suppression carries the weekday roll types the
 * organization ticked beside its holidays; a roll is hidden only when its type
 * is among them — the holiday tick and the roll-type tick intersect. No
 * suppression, or no ticked type, hides nothing.
 *
 * This is the ONE gate every consumer goes through: the live display and the
 * Dashboard (via {@link isRollActiveNow}) and the roll editors' live preview
 * (via {@link getHolidaySuppressionForRollType}), so they cannot disagree.
 */
export function isRollHiddenByHoliday(
  type: unknown,
  suppression: HolidaySuppression | null | undefined
): boolean {
  if (!suppression || !Array.isArray(suppression.rollTypes) || !suppression.rollTypes.length) return false;
  return suppression.rollTypes.includes(normalizeRollType(type));
}

/** The suppression when it hides a roll of this type, else null (for badges). */
export function getHolidaySuppressionForRollType(
  type: unknown,
  suppression: HolidaySuppression | null | undefined
): HolidaySuppression | null {
  return suppression && isRollHiddenByHoliday(type, suppression) ? suppression : null;
}

/** Default display-toggle offsets (overridable per minyan). */
export const BRUCHES_OFFSET_MINUTES = 5;
export const HOIDY_OFFSET_MINUTES = 15;
export const ASHREI_OFFSET_MINUTES = 5;
export const BORCHY_OFFSET_MINUTES = 20;
/** Netz is anchored to the real sunrise zman, so its default offset is Exact. */
export const NETZ_OFFSET_MINUTES = 0;

const MINUTES_PER_DAY = 24 * 60;

/**
 * The minute-of-day the roll display treats as the start of the day. A roll's
 * minyanim are ordered relative to this "midnight" instead of 00:00, so a minyan
 * scheduled before it (e.g. 1:59 AM) sorts after the whole rest of the day
 * rather than leading it. See {@link toRollDaySortKey}.
 */
export const ROLL_DAY_START_MINUTES = 2 * 60; // 02:00

/* ---------------------------------------------------------------------------
 * Strict windows (Weekday Strict / Shabbos Strict)
 * ---------------------------------------------------------------------------
 * Both Strict roll types are on-air for one window of the week, expressed as a
 * start day + time and an end day + time. The window is org-wide and edited in
 * the Minyanim Roll settings modal; the values live on the organization
 * (`weekdayStrict*` / `shabbosStrict*` columns) so the display, the dashboard
 * preview and the editor all read the same boundaries.
 *
 * A window whose end is at or before its start wraps through Saturday night
 * into the next week — which is exactly how the Shabbos default (Friday 12:00 PM
 * → Sunday 12:00 AM) is expressed. Start === end therefore means "the whole
 * week".
 * ------------------------------------------------------------------------- */

/** Day names for the pickers, indexed the same way as JS `Date#getDay()`. */
export const WEEK_DAY_OPTIONS: Array<{ value: number; label: string; short: string }> = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

/** A start→end span of the week, as stored on the organization. */
export interface StrictWindowInput {
  startDay: number;
  startTime: string;
  endDay: number;
  endTime: string;
}

/** The same span resolved to minutes-of-week for comparison. */
export interface StrictWindow extends StrictWindowInput {
  startMinutesOfWeek: number;
  endMinutesOfWeek: number;
  /** True when the window runs through Saturday night into the next week. */
  wraps: boolean;
}

/** Weekday (Strict) default: Sunday 12:00 AM → Friday 12:00 PM. */
export const DEFAULT_WEEKDAY_STRICT_WINDOW: StrictWindowInput = {
  startDay: 0,
  startTime: '00:00',
  endDay: 5,
  endTime: '12:00',
};

/** Shabbos (Strict) default: Friday 12:00 PM → Sunday 12:00 AM. */
export const DEFAULT_SHABBOS_STRICT_WINDOW: StrictWindowInput = {
  startDay: 5,
  startTime: '12:00',
  endDay: 0,
  endTime: '00:00',
};

/** Organization column names backing each window, in one place so the settings
 *  modal, the resolver and the API payload can never disagree. */
export const STRICT_WINDOW_FIELDS = {
  weekday: {
    startDay: 'weekdayStrictStartDay',
    startTime: 'weekdayStrictStartTime',
    endDay: 'weekdayStrictEndDay',
    endTime: 'weekdayStrictEndTime',
  },
  shabbos: {
    startDay: 'shabbosStrictStartDay',
    startTime: 'shabbosStrictStartTime',
    endDay: 'shabbosStrictEndDay',
    endTime: 'shabbosStrictEndTime',
  },
} as const;

/** Coerce anything into a valid day index (Sun=0 … Sat=6), else null. */
export function normalizeWeekDay(value: unknown): number | null {
  const day = Number(value);
  if (!Number.isInteger(day) || day < 0 || day > 6) return null;
  return day;
}

/** Coerce anything into a canonical "HH:MM" clock string, else null. */
export function normalizeClockTime(value: unknown): string | null {
  const minutes = parseClockMinutes(value);
  if (minutes === null) return null;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * Resolve one stored window, falling back field-by-field to `fallback` so a
 * partially-set organization still yields a usable window rather than an
 * arbitrary one.
 */
export function resolveStrictWindow(
  source: Partial<StrictWindowInput> | null | undefined,
  fallback: StrictWindowInput,
): StrictWindow {
  const startDay = normalizeWeekDay(source?.startDay) ?? fallback.startDay;
  const startTime = normalizeClockTime(source?.startTime) ?? fallback.startTime;
  const endDay = normalizeWeekDay(source?.endDay) ?? fallback.endDay;
  const endTime = normalizeClockTime(source?.endTime) ?? fallback.endTime;

  const startMinutesOfWeek = startDay * MINUTES_PER_DAY + (parseClockMinutes(startTime) ?? 0);
  const endMinutesOfWeek = endDay * MINUTES_PER_DAY + (parseClockMinutes(endTime) ?? 0);

  return {
    startDay,
    startTime,
    endDay,
    endTime,
    startMinutesOfWeek,
    endMinutesOfWeek,
    wraps: endMinutesOfWeek <= startMinutesOfWeek,
  };
}

export interface StrictWindows {
  weekday: StrictWindow;
  shabbos: StrictWindow;
}

/**
 * Both Strict windows for an organization. Any organization that has never been
 * through the settings modal — or that is still loading — resolves to the
 * documented defaults, so the display never falls back to a different schedule
 * than the settings screen shows.
 */
export function resolveStrictWindows(organization?: any): StrictWindows {
  const read = (fields: { startDay: string; startTime: string; endDay: string; endTime: string }) => ({
    startDay: organization?.[fields.startDay],
    startTime: organization?.[fields.startTime],
    endDay: organization?.[fields.endDay],
    endTime: organization?.[fields.endTime],
  });

  return {
    weekday: resolveStrictWindow(read(STRICT_WINDOW_FIELDS.weekday), DEFAULT_WEEKDAY_STRICT_WINDOW),
    shabbos: resolveStrictWindow(read(STRICT_WINDOW_FIELDS.shabbos), DEFAULT_SHABBOS_STRICT_WINDOW),
  };
}

/**
 * Whether a minutes-of-week instant falls inside a strict window. The window is
 * half-open — `[start, end)` — so back-to-back windows (the Weekday default ends
 * exactly where the Shabbos default begins) never both claim the same minute.
 */
export function isWithinStrictWindow(minutesOfWeek: number, window: StrictWindow): boolean {
  if (!Number.isFinite(minutesOfWeek) || !window) return false;
  if (window.wraps) {
    // Runs through Saturday night into the next week, e.g. Fri 12:00 → Sun 00:00.
    // start === end is the degenerate case and means the whole week.
    if (window.startMinutesOfWeek === window.endMinutesOfWeek) return true;
    return minutesOfWeek >= window.startMinutesOfWeek || minutesOfWeek < window.endMinutesOfWeek;
  }
  return minutesOfWeek >= window.startMinutesOfWeek && minutesOfWeek < window.endMinutesOfWeek;
}

/**
 * The window as 1–2 linear `[start, end)` spans on the 0…10080-minute week. A
 * wrapping window becomes two spans, which makes overlap and duration questions
 * plain interval arithmetic.
 */
export function getStrictWindowSegments(window: StrictWindow): Array<[number, number]> {
  const week = 7 * MINUTES_PER_DAY;
  if (!window) return [];

  if (window.wraps) {
    if (window.startMinutesOfWeek === window.endMinutesOfWeek) return [[0, week]];
    const segments: Array<[number, number]> = [];
    if (window.startMinutesOfWeek < week) segments.push([window.startMinutesOfWeek, week]);
    if (window.endMinutesOfWeek > 0) segments.push([0, window.endMinutesOfWeek]);
    return segments;
  }

  return [[window.startMinutesOfWeek, window.endMinutesOfWeek]];
}

/** Whether two windows claim any of the same minutes of the week. */
export function strictWindowsOverlap(a: StrictWindow, b: StrictWindow): boolean {
  for (const [aStart, aEnd] of getStrictWindowSegments(a)) {
    for (const [bStart, bEnd] of getStrictWindowSegments(b)) {
      if (aStart < bEnd && bStart < aEnd) return true;
    }
  }
  return false;
}

/** How many minutes of the week a window covers. */
export function getStrictWindowDurationMinutes(window: StrictWindow): number {
  return getStrictWindowSegments(window).reduce((total, [start, end]) => total + (end - start), 0);
}

/** "5 days 12 hrs" — the span of a window, for the settings summary. */
export function formatStrictWindowDuration(window: StrictWindow): string {
  const total = getStrictWindowDurationMinutes(window);
  const days = Math.floor(total / MINUTES_PER_DAY);
  const hours = Math.floor((total % MINUTES_PER_DAY) / 60);
  const minutes = total % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours) parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
  if (minutes) parts.push(`${minutes} min`);
  return parts.length ? parts.join(' ') : '0 min';
}

/** "Friday 12:00 PM" — used in the settings summary and in tooltips. */
export function formatStrictWindowBoundary(day: number, time: string): string {
  const dayLabel = WEEK_DAY_OPTIONS.find((option) => option.value === day)?.label ?? '';
  const minutes = parseClockMinutes(time);
  if (minutes === null) return dayLabel;
  return `${dayLabel} ${formatClockFromMinutes(minutes, { withPeriod: true })}`;
}

/** "Friday 12:00 PM → Sunday 12:00 AM". */
export function formatStrictWindow(window: StrictWindow): string {
  return `${formatStrictWindowBoundary(window.startDay, window.startTime)} → ${formatStrictWindowBoundary(window.endDay, window.endTime)}`;
}

export interface RollContext {
  /** The moment being evaluated. */
  now: Date;
  /** Day of week, Sun=0 … Sat=6. */
  day: number;
  /** Minutes since midnight for `now`. */
  minutesOfDay: number;
  /** Minutes since Sunday 00:00 (day * 1440 + minutesOfDay). */
  minutesOfWeek: number;
  /** True when it is currently Shabbos (Fri after candle-lighting → Sat nightfall). */
  isShabbos: boolean;
  /** True on Rosh Chodesh. */
  isRoshChodesh: boolean;
  /** True on Yom Tov. */
  isYomTov: boolean;
  /** Org-wide Weekday/Shabbos Strict windows this tick is measured against. */
  strictWindows: StrictWindows;
  /**
   * The holiday currently in effect per Settings → Holidays, or null when none
   * is. It carries the weekday roll types the organization chose to hide
   * (`rollTypes`); {@link isRollHiddenByHoliday} decides per roll. Resolved by
   * the caller from the organization's ticks and the Jewish calendar (see
   * utils/holiday_schedule.ts), because both live in the display layer.
   */
  holidaySuppression: HolidaySuppression | null;
}

/**
 * Build the context the RollType filters operate on. `isShabbos`,
 * `isRoshChodesh` and `isYomTov` are supplied by the caller because they
 * depend on zmanim / the Jewish calendar, which live in the display layer.
 *
 * `day` / `minutesOfDay` may also be supplied, and should be whenever the caller
 * knows the SCREEN's timezone: `now` is an absolute instant, and reading the
 * weekday and clock off it with the display device's own timezone would put a
 * screen on the wrong side of a Strict boundary whenever the two differ. They
 * fall back to the device clock only when the caller has nothing better.
 */
export function buildRollContext(input: {
  now: Date;
  day?: number;
  minutesOfDay?: number;
  isShabbos?: boolean;
  isRoshChodesh?: boolean;
  isYomTov?: boolean;
  /** Organization carrying the strict-window columns. */
  organization?: any;
  /** Pre-resolved windows; wins over `organization` when both are given. */
  strictWindows?: StrictWindows;
  /** The holiday hiding the weekday rolls right now, when one is. */
  holidaySuppression?: HolidaySuppression | null;
}): RollContext {
  const now = input.now instanceof Date && !Number.isNaN(input.now.getTime()) ? input.now : new Date();
  const day = normalizeWeekDay(input.day) ?? now.getDay();
  const rawMinutes = Number(input.minutesOfDay);
  const minutesOfDay = Number.isFinite(rawMinutes) && rawMinutes >= 0 && rawMinutes < MINUTES_PER_DAY
    ? Math.floor(rawMinutes)
    : now.getHours() * 60 + now.getMinutes();
  const minutesOfWeek = day * MINUTES_PER_DAY + minutesOfDay;
  const strictWindows = input.strictWindows || resolveStrictWindows(input.organization);

  return {
    now,
    day,
    minutesOfDay,
    minutesOfWeek,
    // Fallback used only when the caller cannot compute real Shabbos: treat the
    // configured Shabbos (Strict) window as the changeover, so the two agree.
    isShabbos:
      input.isShabbos !== undefined
        ? Boolean(input.isShabbos)
        : isWithinStrictWindow(minutesOfWeek, strictWindows.shabbos),
    isRoshChodesh: Boolean(input.isRoshChodesh),
    isYomTov: Boolean(input.isYomTov),
    strictWindows,
    holidaySuppression: input.holidaySuppression || null,
  };
}

/** Map any stored/legacy value to a canonical {@link RollType}. */
export function normalizeRollType(type: unknown): RollType {
  const raw = String(type ?? '').trim().toLowerCase();
  switch (raw) {
    case 'weekday_strict':
    case 'weekday-strict':
    case 'weekdaystrict':
      return RollType.WeekdayStrict;
    case 'weekday_custom':
    case 'weekday-custom':
    case 'weekdaycustom':
      return RollType.WeekdayCustom;
    case 'shabbos_strict':
    case 'shabbos-strict':
    case 'shabbosstrict':
    case 'shabbat_strict':
      return RollType.ShabbosStrict;
    case 'shabbos':
    case 'shabbat':
      return RollType.ShabbosAlways;
    case 'rosh_chodesh':
    case 'rosh-chodesh':
    case 'roshchodesh':
      return RollType.RoshChodesh;
    case 'yom_tov':
    case 'yomtov':
      return RollType.YomTov;
    case 'custom':
      return RollType.Custom;
    case 'weekday':
    case '':
    default:
      return RollType.WeekdayAlways;
  }
}

/**
 * RollType scheduling filter — implements the spec's "Developer Logic":
 *
 *   1 Weekday (Always)  → always on screen (live only inside the Weekday
 *                         Strict window — see isRollLiveForType)
 *   2 Weekday (Strict)  → inside the org's Weekday Strict window
 *                         (default Sunday 12:00 AM → Friday 12:00 PM)
 *   3 Shabbos (Always)  → always on screen (times lock to the upcoming Shabbos
 *                         and regenerate each Sunday 00:00 local — see the
 *                         Shabbos Weekly Static Lock anchor in Display.vue;
 *                         live only inside the Shabbos Strict window)
 *   4 Shabbos (Strict)  → inside the org's Shabbos Strict window
 *                         (default Friday 12:00 PM → Sunday 12:00 AM)
 *   5 Rosh Chodesh      → jewishCalendar.isRoshChodesh()
 *
 * Both Strict windows are org-wide settings edited in the Minyanim Roll
 * settings modal; see {@link resolveStrictWindows}.
 */
export function isRollActiveForType(type: unknown, ctx: RollContext): boolean {
  const strictWindows = ctx?.strictWindows || resolveStrictWindows(null);

  switch (normalizeRollType(type)) {
    case RollType.WeekdayAlways:
      // "Always" means always on screen — over Shabbos too, next to the
      // Shabbos roll — never only outside Shabbos. Whether it is LIVE (the
      // highlight, countdown and upcoming card) follows the Weekday (Strict)
      // window instead: see isRollLiveForType.
      return true;
    case RollType.WeekdayStrict:
      return isWithinStrictWindow(ctx.minutesOfWeek, strictWindows.weekday);
    case RollType.ShabbosAlways:
      // "Always" means always displayed, not only during Shabbos. The Shabbos
      // times it shows are locked to the upcoming weekend and recalculated once
      // a week (Sunday 00:00 local), handled by the anchor logic in Display.vue.
      // Use Shabbos (Strict) to limit visibility to the live Shabbos window.
      return true;
    case RollType.ShabbosStrict:
      return isWithinStrictWindow(ctx.minutesOfWeek, strictWindows.shabbos);
    case RollType.RoshChodesh:
      return ctx.isRoshChodesh;
    case RollType.YomTov:
      return ctx.isYomTov;
    case RollType.WeekdayCustom:
      // Real gating uses the roll's recurrence (selected weekdays + optional
      // time window) via isRollActiveNow; by type alone it counts as active.
      return true;
    case RollType.Custom:
    default:
      // Custom rolls are gated by their recurrence pattern, not by date math.
      return true;
  }
}

/**
 * The Strict window a roll of this type runs on, or null when the type is not
 * window-based (Weekday (Custom) follows its recurrence, Rosh Chodesh / Yom
 * Tov the Jewish calendar, Custom is always on):
 *
 *   Weekday (Always) / Weekday (Strict) → the Weekday (Strict) window
 *   Shabbos (Always) / Shabbos (Strict) → the Shabbos (Strict) window
 *
 * For a Strict roll this is when it is on screen at all; for an Always roll it
 * is when it is LIVE (see {@link isRollLiveForType}). The roll editors read it
 * for their "on air / live" hint and for their preview's live state.
 */
export function getRollStrictWindow(type: unknown, windows?: StrictWindows | null): StrictWindow | null {
  const resolved = windows || resolveStrictWindows(null);
  switch (normalizeRollType(type)) {
    case RollType.WeekdayAlways:
    case RollType.WeekdayStrict:
      return resolved.weekday;
    case RollType.ShabbosAlways:
    case RollType.ShabbosStrict:
      return resolved.shabbos;
    default:
      return null;
  }
}

/**
 * Whether a roll of this type is LIVE at this tick — its schedule in force —
 * as opposed to merely on screen ({@link isRollActiveForType}). The two differ
 * only for the "Always" types:
 *
 *   Weekday (Always)  → live inside the org's Weekday (Strict) window
 *   Shabbos (Always)  → live inside the org's Shabbos (Strict) window
 *
 * Outside its window an Always roll stays on screen as a plain schedule, but
 * nothing on it runs: no next-minyan highlight, no started colour, no
 * {countdown}, no upcoming-minyan card, no smart-display window, and it takes
 * no part in the priority contest. Over Shabbos (say Friday 2:00 PM → Sunday
 * 12:00 AM) the weekday schedule is still readable, yet it never counts down
 * to a weekday minyan that is not happening. Every other type is live exactly
 * when it is on screen.
 */
export function isRollLiveForType(type: unknown, ctx: RollContext): boolean {
  const window = getRollStrictWindow(type, ctx?.strictWindows);
  if (window) return isWithinStrictWindow(ctx.minutesOfWeek, window);
  return isRollActiveForType(type, ctx);
}

/** Parse an "HH:MM" clock string into minutes-of-day, or null when invalid. */
function parseClockMinutes(value: unknown): number | null {
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * One selected weekday's activation window inside a Weekday (Custom) roll:
 * either the whole day, or a start→end range expressed in minutes-of-day.
 */
export type WeekdayCustomDayWindow =
  | { fullDay: true }
  | { fullDay: false; start: number; end: number };

/**
 * The window a Weekday (Custom) roll is active in on `day` (Sun=0 … Sat=6).
 *
 * Per-day windows live in `recurrence.dayTimes`, keyed by the day number
 * (`{ 1: { fullDay, startTime, endTime } }`). A day marked `fullDay` — and any
 * day whose times are missing or unparseable, which is how rolls saved before
 * per-day windows existed read — falls back to the roll-wide startTime/endTime
 * and, failing that, to the whole day.
 */
export function resolveWeekdayCustomDayWindow(recurrence: any, day: number): WeekdayCustomDayWindow {
  const dayTimes = recurrence?.dayTimes;
  const entry = dayTimes ? (dayTimes[day] ?? dayTimes[String(day)]) : null;
  if (entry?.fullDay === true) return { fullDay: true };

  const start = parseClockMinutes(entry?.startTime) ?? parseClockMinutes(recurrence?.startTime);
  const end = parseClockMinutes(entry?.endTime) ?? parseClockMinutes(recurrence?.endTime);
  // Incomplete window → the roll simply runs all day on that weekday.
  if (start === null || end === null) return { fullDay: true };
  return { fullDay: false, start, end };
}

/** Selected weekdays (Sun=0 … Sat=6), de-duplicated and numeric. */
function getRecurrenceDays(recurrence: any): number[] {
  if (!Array.isArray(recurrence?.daysOfWeek)) return [];
  const days = recurrence.daysOfWeek
    .map((d: unknown) => Number(d))
    .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6);
  return Array.from(new Set<number>(days));
}

/**
 * "Weekday (Custom)" scheduling: the roll is active on the chosen weekdays
 * (recurrence.daysOfWeek, Sun=0 … Sat=6) — the whole day when
 * `recurrence.timeMode` is 'fixed', or within each day's own start→end window
 * when it is 'range'. A window whose end is at or before its start
 * (e.g. 20:00 → 06:00) wraps past midnight into the next calendar day, so the
 * previous weekday's window is checked too.
 */
export function isWeekdayCustomActive(recurrence: any, ctx: RollContext): boolean {
  const days = getRecurrenceDays(recurrence);
  // No selected day → nothing to show.
  if (days.length === 0) return false;

  // 'fixed' (or unset) → active the whole day on each selected weekday.
  if (recurrence?.timeMode !== 'range') return days.includes(ctx.day);

  if (days.includes(ctx.day)) {
    const today = resolveWeekdayCustomDayWindow(recurrence, ctx.day);
    if (today.fullDay) return true;
    if (today.end > today.start) {
      if (ctx.minutesOfDay >= today.start && ctx.minutesOfDay < today.end) return true;
    } else if (ctx.minutesOfDay >= today.start) {
      // Wraps past midnight: [start, 24:00) still belongs to today.
      return true;
    }
  }

  // …and the tail [00:00, end) of a window that started on the previous day.
  const previousDay = (ctx.day + 6) % 7;
  if (days.includes(previousDay)) {
    const previous = resolveWeekdayCustomDayWindow(recurrence, previousDay);
    if (!previous.fullDay && previous.end <= previous.start && ctx.minutesOfDay < previous.end) {
      return true;
    }
  }

  return false;
}

/* -------------------------------------------------------------------------
 * Weekday (Custom) editor helpers
 *
 * The roll editors (Add / Edit / Duplicate) all drive the same inline schedule
 * widget, so the serialize + validate rules live here next to the read side
 * above — one place decides what a Weekday (Custom) recurrence looks like.
 * ---------------------------------------------------------------------- */

/** Weekday names indexed the way the recurrence stores them (Sun=0 … Sat=6). */
export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** One weekday's persisted window inside a Weekday (Custom) recurrence. */
export interface WeekdayCustomDayTime {
  fullDay: boolean;
  startTime: string;
  endTime: string;
}

const DEFAULT_WINDOW_START = '09:00';
const DEFAULT_WINDOW_END = '17:00';

/**
 * The per-day windows a brand-new Weekday (Custom) roll starts with: every
 * weekday ticked "Full day", carrying the default window underneath so
 * unticking one gives a sensible start/end instead of a blank pair. Rolls
 * loaded from the database keep whatever they were saved with — this is only
 * the seed the Add/Duplicate editors hand the schedule widget.
 */
export function createDefaultWeekdayCustomDayTimes(): Record<string, WeekdayCustomDayTime> {
  const dayTimes: Record<string, WeekdayCustomDayTime> = {};
  for (let day = 0; day <= 6; day += 1) {
    dayTimes[String(day)] = {
      fullDay: true,
      startTime: DEFAULT_WINDOW_START,
      endTime: DEFAULT_WINDOW_END,
    };
  }
  return dayTimes;
}

/** The per-day entry stored for `day`, whichever key shape it was saved under. */
function getDayTimeEntry(schedule: any, day: number): any {
  const source = schedule?.dayTimes;
  if (!source || typeof source !== 'object') return {};
  return source[day] ?? source[String(day)] ?? {};
}

/**
 * Serialize the editor's Weekday (Custom) schedule into the `recurrence` shape
 * {@link isWeekdayCustomActive} reads. Per-day windows are written for every
 * weekday (not just the selected ones) so unchecking a day and checking it
 * again keeps the times that were set for it; `daysOfWeek` alone decides which
 * days actually run. Fixed (whole-day) rolls store no window at all.
 */
export function buildWeekdayCustomRecurrence(schedule: any): any {
  const days = getRecurrenceDays(schedule);
  const isRange = schedule?.timeMode === 'range';

  if (!isRange) {
    return {
      type: 'weekly',
      daysOfWeek: days,
      timeMode: 'fixed',
      startTime: null,
      endTime: null,
      dayTimes: null,
    };
  }

  // Roll-wide window: the fallback a day without its own times inherits.
  const fallbackStart = normalizeClockTime(schedule?.startTime) ?? DEFAULT_WINDOW_START;
  const fallbackEnd = normalizeClockTime(schedule?.endTime) ?? DEFAULT_WINDOW_END;

  const dayTimes: Record<string, WeekdayCustomDayTime> = {};
  for (let day = 0; day <= 6; day += 1) {
    const entry = getDayTimeEntry(schedule, day);
    dayTimes[String(day)] = {
      fullDay: entry?.fullDay === true,
      // Times are kept even for a "Full day" weekday, so unticking it later
      // restores the window instead of coming back blank.
      startTime: normalizeClockTime(entry?.startTime) ?? fallbackStart,
      endTime: normalizeClockTime(entry?.endTime) ?? fallbackEnd,
    };
  }

  return {
    type: 'weekly',
    daysOfWeek: days,
    timeMode: 'range',
    startTime: fallbackStart,
    endTime: fallbackEnd,
    dayTimes,
  };
}

/**
 * Guard the editor's Weekday (Custom) schedule before save: at least one active
 * weekday, and — in the start/end mode — a complete window on every active day
 * that is not marked "Full day". Returns the message to show the user, or null
 * when the schedule is good to save.
 */
export function validateWeekdayCustomSchedule(schedule: any): string | null {
  const days = getRecurrenceDays(schedule);
  if (days.length === 0) {
    return 'Select at least one active day for the Weekday (Custom) roll.';
  }
  if (schedule?.timeMode !== 'range') return null;

  const fallbackStart = normalizeClockTime(schedule?.startTime);
  const fallbackEnd = normalizeClockTime(schedule?.endTime);

  for (const day of days) {
    const entry = getDayTimeEntry(schedule, day);
    if (entry?.fullDay === true) continue;
    // A day that was never given its own times inherits the roll-wide window;
    // one whose field was cleared has to be filled back in.
    const start = entry?.startTime == null ? fallbackStart : normalizeClockTime(entry.startTime);
    const end = entry?.endTime == null ? fallbackEnd : normalizeClockTime(entry.endTime);
    if (start === null || end === null) {
      return `Set a start and end time for ${WEEKDAY_LABELS[day]}, or tick Full day.`;
    }
  }

  return null;
}

/**
 * Whether a roll is active right now. Weekday (Custom) rolls are gated by their
 * recurrence (selected weekdays + optional time window); every other type is
 * gated purely by {@link isRollActiveForType}.
 *
 * A holiday the organization ticked in Settings → Holidays outranks both: while
 * one is in effect, every roll whose TYPE is also ticked there — Weekday
 * (Strict) / (Custom) / (Always) — is off the air no matter what its own
 * schedule says, so the holiday schedule has the screen to itself.
 */
export function isRollActiveNow(roll: any, ctx: RollContext): boolean {
  const type = normalizeRollType(roll?.type);
  if (isRollHiddenByHoliday(type, ctx?.holidaySuppression)) return false;
  if (type === RollType.WeekdayCustom) {
    return isWeekdayCustomActive(roll?.recurrence, ctx);
  }
  return isRollActiveForType(type, ctx);
}

/**
 * Whether a roll is LIVE right now: on screen ({@link isRollActiveNow} — its
 * own schedule, and no holiday hiding it) and, for an Always roll, inside its
 * Strict window ({@link isRollLiveForType}). The one gate for everything that
 * runs on a roll — the next-minyan highlight, the started colour, the
 * {countdown}, the upcoming-minyan card, the smart-display window — and for
 * whether it competes on priority ({@link getActiveRolls}). The live display
 * and the roll editors' preview both decide through this, so they agree.
 */
export function isRollLiveNow(roll: any, ctx: RollContext): boolean {
  if (!isRollActiveNow(roll, ctx)) return false;
  return isRollLiveForType(roll?.type, ctx);
}

type GetMinutes = (minyan: any) => number;

function getRollMinyanim(roll: any): any[] {
  return Array.isArray(roll?.minyanim) ? roll.minyanim : [];
}

function getRollPriority(roll: any): number {
  const p = Number(roll?.priority);
  return Number.isFinite(p) ? p : 0;
}

function isTextMinyan(minyan: any): boolean {
  return minyan?.itemType === 'text' || minyan?.kind === 'text' || minyan?.type === 'text';
}

/**
 * The roll editor's per-minyan Active switch: switching a single row off hides
 * it from the display and the live preview while it stays in the roll.
 *
 * Absent = active, so every minyan saved before the switch existed — and every
 * minyan generated by an automated rule, which carries no such field — keeps
 * showing. Never test this with `=== true`.
 *
 * Deliberately not applied inside {@link getRollMinyanim}: that also feeds
 * {@link rollUsesManualOrder}, which must keep seeing the roll as it was
 * authored. The filtering happens in {@link getVisibleMinyanim} — after that
 * ordering decision — plus at each editor's live preview.
 */
export function isMinyanActive(minyan: any): boolean {
  return minyan?.isActive !== false;
}

/** A Fixed Time minyan (never a text block). A row saved without a timing mode
 * is one: that is the column's default, and how the editors load it. */
function isFixedTimeMinyan(minyan: any): boolean {
  if (!minyan || isTextMinyan(minyan)) return false;
  const mode = minyan.timingMode;
  return mode === undefined || mode === null || mode === '' || mode === 'fixed';
}

/**
 * The roll editor's "Display" tick beside a Fixed Time minyan's Time. Unticked,
 * the minyan's time is never printed — the card or list time and every {time},
 * on the display and the live preview alike — so it shows just its name and
 * room, and the editor disables the time inputs and stops requiring a time.
 * The time they hold is kept: it still sorts the minyan and drives its
 * highlight and countdown; only its clock is kept off the screen.
 *
 * Absent = shown, so minyanim saved before the tick existed and generated
 * (automated) minyanim keep their time. Dynamic / Weekly Static Zman minyanim
 * have no tick and always show theirs, whatever a row switched away from Fixed
 * Time still carries.
 */
export function isMinyanTimeDisplayed(minyan: any): boolean {
  return minyan?.displayTime !== false || !isFixedTimeMinyan(minyan);
}

/**
 * Whether an editor row's Fixed Time is blank: its 12-hour `hour` or its
 * `minute` is null / '' (a row saved with Display unticked and no time loads
 * that way). A row without those fields is never blank — it falls back to its
 * stored `fixedTime`.
 */
export function isMinyanFixedTimeBlank(minyan: any): boolean {
  if (!isFixedTimeMinyan(minyan)) return false;
  const isBlank = (value: any) => value === null || value === '';
  return isBlank(minyan.hour) || isBlank(minyan.minute);
}

/** A Fixed Time row whose time is displayed but blank: the editors won't save it. */
export function isMinyanFixedTimeMissing(minyan: any): boolean {
  return isMinyanTimeDisplayed(minyan) && isMinyanFixedTimeBlank(minyan);
}

/** A text block with "None (manual)" timing — it has no time to sort by and is
 * positioned purely by hand (drag order). */
function isManualTextMinyan(minyan: any): boolean {
  return isTextMinyan(minyan) && (
    minyan?.manual === true ||
    minyan?.timingMode === 'none' ||
    minyan?.textTiming?.timeType === 'none'
  );
}

/**
 * A roll whose minyanim are arranged by hand (drag order) rather than auto-
 * sorted by time: every Shabbos roll, plus any roll that contains a manual-time
 * text block. Their display/preview order follows each minyan's persisted
 * `order` field so the screen matches exactly how they were dragged in the editor.
 */
export function rollUsesManualOrder(roll: any): boolean {
  const type = roll?.type;
  if (type === 'shabbos' || type === 'shabbos_strict') return true;
  // Decided from the roll as it was AUTHORED, so a row switched off by the
  // per-minyan Active toggle still counts. Otherwise hiding the one manual-time
  // text block would silently re-sort the whole roll by time on screen while the
  // editor and its live preview — which both read the unfiltered list — kept
  // showing the drag order. Callers that hand us an already-filtered `minyanim`
  // pass the authored list as `_orderSourceMinyanim`.
  const source = Array.isArray(roll?._orderSourceMinyanim)
    ? roll._orderSourceMinyanim
    : getRollMinyanim(roll);
  return source.some(isManualTextMinyan);
}

/** Persisted drag position; minyanim without an explicit order fall to the end
 * (e.g. automated/generated minyanim appended after the manual ones). */
function getMinyanOrder(minyan: any): number {
  const n = Number(minyan?.order);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

/**
 * The rolls on screen right now, highest priority first.
 *
 * Every roll that is switched on and on screen per its type is a candidate.
 * Priority — "Greatest Wins" — is then settled between the rolls that are LIVE
 * at this tick ({@link isRollLiveNow}): only the live roll(s) carrying the
 * highest priority stay; a live roll outranked by another goes off the air
 * whole. A Rosh Chodesh roll at priority 2 therefore replaces the weekday
 * rolls at priority 1 for the day, instead of sharing the screen with their
 * leftovers; equal priorities share the screen.
 *
 * A roll on screen but not live — an Always roll outside its Strict window,
 * i.e. the weekday schedule over Shabbos or the Shabbos schedule midweek — is
 * a schedule on the wall: it neither outranks nor is outranked, so it stays up
 * next to whatever is live.
 */
export function getActiveRolls(rolls: any[], ctx: RollContext): any[] {
  const onScreen = (Array.isArray(rolls) ? rolls : [])
    .filter((roll) => roll && roll.isActive !== false && isRollActiveNow(roll, ctx));

  let topLivePriority = Number.NEGATIVE_INFINITY;
  for (const roll of onScreen) {
    if (isRollLiveNow(roll, ctx)) topLivePriority = Math.max(topLivePriority, getRollPriority(roll));
  }

  return onScreen
    .filter((roll) => !isRollLiveNow(roll, ctx) || getRollPriority(roll) >= topLivePriority)
    .sort((a, b) => getRollPriority(b) - getRollPriority(a));
}

/**
 * Remap a minutes-of-day value onto the roll display's day, which starts at
 * {@link ROLL_DAY_START_MINUTES} (2:00 AM) rather than at midnight:
 * 2:00 AM → 0, 7:00 AM → 300, midnight → 1320, 1:59 AM → 1439. Sorting by this
 * key is what pushes pre-2:00 AM minyanim to the very end of a roll.
 *
 * Values outside [0, 1440) wrap first, so a time rounded up to 24:00 sorts where
 * its displayed 12:00 AM clock says it belongs. Times that cannot be resolved
 * (Infinity / NaN) stay Infinity so they still sink last.
 *
 * Ordering only — a minyan's stored time and the clock shown for it are never
 * touched by this.
 */
export function toRollDaySortKey(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return Number.POSITIVE_INFINITY;
  const minutesOfDay = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return (minutesOfDay - ROLL_DAY_START_MINUTES + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * Comparator for two resolved minutes-of-day values on the roll's 2:00 AM day
 * ({@link toRollDaySortKey}). Unresolvable times compare equal to one another
 * and after every real time, so an Infinity/NaN can never return NaN and
 * corrupt the sort. Equal times compare 0, which keeps `Array#sort` stable — the
 * incoming order (drag order / generation order) breaks the tie.
 *
 * The single comparator behind the live display, the roll editor's live preview
 * and the dashboard's embedded screen preview, so all three order identically.
 */
export function compareRollDayMinutes(a: number, b: number): number {
  const aKey = toRollDaySortKey(a);
  const bKey = toRollDaySortKey(b);
  if (aKey === bKey) return 0;
  if (!Number.isFinite(aKey)) return 1;
  if (!Number.isFinite(bKey)) return -1;
  return aKey - bKey;
}

/* ---------------------------------------------------------------------------
   Smart display — show only the minyanim around the current time.

   A roll normally puts its whole day on screen. With smart display on, the
   screen keeps to what matters now, one of two ways:

     'time'  — a minyan appears `showBeforeMinutes` before it starts and stays
               `keepAfterMinutes` after. Optionally the next minyan is kept on
               screen while nothing is inside that window, so the board is
               never blank between minyanim.
     'count' — the next `upcomingCount` minyanim that have not started, plus the
               last `pastCount` that have — each of those dropped
               `pastExpiryMinutes` after it started so it cannot linger all day.

   Time is measured on the roll day (2:00 AM to 2:00 AM, the day the roll is
   ordered on): a minyan is "upcoming" until its start minute and "started"
   from then until the day ends — never "due again tomorrow", which is what
   used to bring already-started minyanim back on screen. Only the time
   window's "never leave the screen empty" fallback looks past the day, to
   tomorrow's first minyan once every minyan of today has started. Entries
   with no resolvable time (a text block with no zman) have no place on the
   clock and are never windowed out.

   Every consumer must read "now" on the screen's clock (its timezone), the
   same clock the minyan times are resolved on.

   Lives here so the live display, the editor's live preview and the Minyanim
   tab's "on screen right now" summary apply one identical rule.
   --------------------------------------------------------------------------- */

export type SmartDisplayMode = 'time' | 'count';

export interface SmartDisplaySettings {
  /** Off = the whole roll shows all day (the behaviour before this existed). */
  enabled: boolean;
  mode: SmartDisplayMode;
  /** Time window: a minyan appears this many minutes before it starts ... */
  showBeforeMinutes: number;
  /** ... and stays on screen this many minutes after it started. */
  keepAfterMinutes: number;
  /** Time window: while nothing is inside the window, keep the next minyan up. */
  keepNextWhenIdle: boolean;
  /** Count window: how many not-yet-started minyanim to show. */
  upcomingCount: number;
  /** Count window: how many already-started minyanim to keep ... */
  pastCount: number;
  /** ... and for how many minutes after each started. */
  pastExpiryMinutes: number;
}

export const SMART_DISPLAY_DEFAULTS: SmartDisplaySettings = {
  enabled: false,
  mode: 'time',
  showBeforeMinutes: 30,
  keepAfterMinutes: 20,
  keepNextWhenIdle: true,
  upcomingCount: 2,
  pastCount: 1,
  pastExpiryMinutes: 20,
};

/** Editor bounds. Minutes stop at half a day — past that the window is the whole roll. */
export const SMART_DISPLAY_LIMITS = {
  showBeforeMinutes: { min: 0, max: 720 },
  keepAfterMinutes: { min: 0, max: 720 },
  upcomingCount: { min: 1, max: 20 },
  pastCount: { min: 0, max: 20 },
  pastExpiryMinutes: { min: 1, max: 720 },
} as const;

export type SmartDisplayNumberField = keyof typeof SMART_DISPLAY_LIMITS;

function clampSmartNumber(value: unknown, field: SmartDisplayNumberField): number {
  const { min, max } = SMART_DISPLAY_LIMITS[field];
  const n = Number(value);
  if (!Number.isFinite(n)) return SMART_DISPLAY_DEFAULTS[field];
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Coerce whatever is stored (or half-typed) into a complete, in-range settings
 * object. Every consumer reads through this, so a roll saved before a field
 * existed, or a value someone hand-edited out of range, is still usable.
 */
export function normalizeSmartDisplay(source?: any): SmartDisplaySettings {
  const src = source && typeof source === 'object' ? source : {};
  return {
    enabled: src.enabled === true || src.enabled === 'true',
    mode: src.mode === 'count' ? 'count' : 'time',
    showBeforeMinutes: clampSmartNumber(src.showBeforeMinutes, 'showBeforeMinutes'),
    keepAfterMinutes: clampSmartNumber(src.keepAfterMinutes, 'keepAfterMinutes'),
    keepNextWhenIdle: src.keepNextWhenIdle === undefined
      ? SMART_DISPLAY_DEFAULTS.keepNextWhenIdle
      : src.keepNextWhenIdle === true || src.keepNextWhenIdle === 'true',
    upcomingCount: clampSmartNumber(src.upcomingCount, 'upcomingCount'),
    pastCount: clampSmartNumber(src.pastCount, 'pastCount'),
    pastExpiryMinutes: clampSmartNumber(src.pastExpiryMinutes, 'pastExpiryMinutes'),
  };
}

/** True when a roll's design asks for the window to be applied. */
export function isSmartDisplayEnabled(design: any): boolean {
  return normalizeSmartDisplay(design?.smartDisplay).enabled;
}

/**
 * Whether one entry follows the roll's smart-display window. The per-minyan
 * `smartDisplay` tick in the Minyanim tab; absent (older rows, generated
 * minyanim, preview rows) means yes, so turning the feature on reaches every
 * minyan until someone unticks one. An unticked minyan is on screen all day.
 */
export function isMinyanSmartDisplayIncluded(minyan: any): boolean {
  return minyan?.smartDisplay !== false;
}

/**
 * The settings that govern one entry, when they are its own rather than its
 * roll's. Automated minyanim carry the settings of the rule that generated
 * them ({@link generateAutomatedMinyanim}), so each rule's rows are windowed
 * on their own terms; everything else answers to the roll.
 */
export function getMinyanSmartDisplayRule(minyan: any): any {
  const own = minyan?.smartDisplayRule;
  return own && typeof own === 'object' ? own : null;
}

/** Group key for entries that answer to the roll rather than to their own rule. */
const ROLL_SMART_GROUP = '__roll';

/**
 * Where a start time sits relative to now on the roll day — 2:00 AM to 2:00 AM,
 * the same day the roll is ordered on ({@link toRollDaySortKey}). Positive =
 * starts in that many minutes, 0 = starting this minute, negative = started
 * that many minutes ago. NaN when either side is unresolved.
 *
 * Deliberately NOT a cyclic clock. On a cyclic clock a 3:33 PM minyan seen at
 * 4:05 PM reads as "due again in 23 h 28 min", so it kept re-qualifying as an
 * upcoming minyan and "hide started after N min" could never take it off the
 * screen. Here a minyan that has started stays started until the roll day ends.
 */
export function rollDayMinutesFromNow(startMinutes: number, nowMinutes: number): number {
  if (!Number.isFinite(startMinutes) || !Number.isFinite(nowMinutes)) return Number.NaN;
  return toRollDaySortKey(Math.round(startMinutes)) - toRollDaySortKey(Math.round(nowMinutes));
}

export interface SmartDisplayTiming {
  /** Minutes until it starts on this roll day; 0 once it has started. */
  until: number;
  /** Minutes since it started on this roll day; 0 while it has not. */
  since: number;
  /** True from its start minute until the roll day ends at 2:00 AM. */
  started: boolean;
}

/** One entry's timing on the roll day, exactly as the smart-display rules read it. */
export function getSmartDisplayTiming(startMinutes: number, nowMinutes: number): SmartDisplayTiming {
  const delta = rollDayMinutesFromNow(startMinutes, nowMinutes);
  if (!Number.isFinite(delta)) return { until: Number.NaN, since: Number.NaN, started: false };
  return delta > 0
    ? { until: delta, since: 0, started: false }
    : { until: 0, since: -delta, started: true };
}

interface SmartEntry extends SmartDisplayTiming {
  index: number;
}

/** Not yet started today, soonest first. Ties keep the roll's own order. */
function sortUpcoming(entries: SmartEntry[]): SmartEntry[] {
  return entries
    .filter((entry) => !entry.started)
    .sort((a, b) => a.until - b.until || a.index - b.index);
}

/**
 * The entry a board falls back to while nothing is inside its window: the
 * earliest minyan still to come today, or — once every minyan of the day has
 * started — the earliest of the roll day, i.e. tomorrow's first. Never a
 * started minyan brought back as "next".
 */
function pickNextEntry(entries: SmartEntry[]): SmartEntry | undefined {
  const upcoming = sortUpcoming(entries);
  if (upcoming.length) return upcoming[0];
  return entries.slice().sort((a, b) => b.since - a.since || a.index - b.index)[0];
}

function toSmartEntries<T>(
  items: T[],
  nowMinutes: number,
  getMinutes: (item: T) => number,
  isIncluded: (item: T) => boolean
): SmartEntry[] {
  const entries: SmartEntry[] = [];
  items.forEach((item, index) => {
    const minutes = getMinutes(item);
    if (!Number.isFinite(minutes) || !isIncluded(item)) return;
    const timing = getSmartDisplayTiming(minutes, nowMinutes);
    if (Number.isFinite(timing.until)) entries.push({ index, ...timing });
  });
  return entries;
}

/**
 * The minyan a board would announce as "next" at this minute — the same pick
 * {@link applySmartDisplayWindow} falls back to while nothing is in window, so
 * the editor's "on screen now" summary names what the display would show.
 */
export function pickSmartDisplayNext<T>(
  items: T[],
  nowMinutes: number,
  getMinutes: (item: T) => number,
  isIncluded: (item: T) => boolean = () => true
): T | undefined {
  const list = Array.isArray(items) ? items : [];
  if (!list.length || !Number.isFinite(nowMinutes)) return undefined;
  const next = pickNextEntry(toSmartEntries(list, nowMinutes, getMinutes, isIncluded));
  return next ? list[next.index] : undefined;
}

/**
 * Apply smart-display windows to a roll's (already ordered) entries at a given
 * minute of the day. Returns the entries that belong on screen, in the order
 * they came in; returns the input untouched when nothing is windowing, so
 * callers can pass it through unconditionally.
 *
 * `nowMinutes` must be read on the clock the roll's times are in — the
 * screen's timezone — or the window lands on the wrong minyanim.
 *
 * Entries are grouped by whose settings govern them — the roll's, or an
 * automated rule's own — and each group is windowed independently, so one
 * rule's "next 2" never eats another's allowance.
 *
 * `isIncluded` says which entries answer to a window at all. The rest are on
 * screen regardless and take no part in the counting.
 */
export function applySmartDisplayWindow<T>(
  items: T[],
  settings: any,
  nowMinutes: number,
  getMinutes: (item: T) => number,
  isIncluded: (item: T) => boolean = () => true
): T[] {
  const list = Array.isArray(items) ? items : [];
  if (!list.length || !Number.isFinite(nowMinutes)) return list;

  // Nothing to do when the roll is not windowing and no entry brings a rule of
  // its own — the common case, kept to a single pass.
  const rollSettings = normalizeSmartDisplay(settings);
  if (!rollSettings.enabled && !list.some((item) => getMinyanSmartDisplayRule(item))) return list;

  const groups = new Map<any, { settings: SmartDisplaySettings; entries: SmartEntry[] }>();
  const keep = new Set<number>();

  list.forEach((item, index) => {
    const minutes = getMinutes(item);
    // Untimed entries have no place on the clock, and opted-out ones asked not
    // to be windowed. Both stay, and neither is counted.
    if (!Number.isFinite(minutes) || !isIncluded(item)) {
      keep.add(index);
      return;
    }
    const own = getMinyanSmartDisplayRule(item);
    const key = own ? ((item as any).__ruleId || own) : ROLL_SMART_GROUP;
    let group = groups.get(key);
    if (!group) {
      group = { settings: own ? normalizeSmartDisplay(own) : rollSettings, entries: [] };
      groups.set(key, group);
    }
    group.entries.push({ index, ...getSmartDisplayTiming(minutes, nowMinutes) });
  });

  groups.forEach(({ settings: s, entries }) => {
    if (!s.enabled) {
      entries.forEach((entry) => keep.add(entry.index));
      return;
    }

    if (s.mode === 'count') {
      // The next N still to come today.
      sortUpcoming(entries)
        .slice(0, s.upcomingCount)
        .forEach((entry) => keep.add(entry.index));
      // The last M that started today and have not yet expired, most recent
      // first (ties: the later one in roll order is the more recent).
      entries
        .filter((entry) => entry.started && entry.since < s.pastExpiryMinutes)
        .sort((a, b) => a.since - b.since || b.index - a.index)
        .slice(0, s.pastCount)
        .forEach((entry) => keep.add(entry.index));
      return;
    }

    // Time window: on screen from `showBefore` minutes before the start until
    // `keepAfter` minutes after it.
    const inWindow = entries.filter((entry) =>
      entry.started ? entry.since < s.keepAfterMinutes : entry.until <= s.showBeforeMinutes
    );
    inWindow.forEach((entry) => keep.add(entry.index));

    if (!inWindow.length && s.keepNextWhenIdle) {
      const next = pickNextEntry(entries);
      if (next) keep.add(next.index);
    }
  });

  return list.filter((item, index) => keep.has(index));
}

/**
 * The minyanim one roll shows, in display order. Rows switched off in the roll
 * editor are dropped; every other row stays — a minyan is never hidden because
 * another roll has one at the same time. Priority is settled between whole
 * rolls, in {@link getActiveRolls}.
 *
 * Order: manually-arranged rolls (Shabbos, or any roll with a manual text
 * block) keep the hand-picked drag order (the persisted `order` field) so the
 * display matches the editor exactly; all other rolls are sorted by time on the
 * 2:00 AM day (see {@link compareRollDayMinutes}), so a 1:59 AM minyan lands at
 * the end of the roll instead of the start.
 *
 * `nowMinutes` — the screen's current minute of the day — lets the roll's smart
 * display window ({@link applySmartDisplayWindow}) trim the result to what is
 * relevant right now, but only while the roll is `live` ({@link isRollLiveNow}):
 * a roll on screen outside its live window is a plain schedule and shows every
 * minyan. Left out, the whole ordered roll comes back.
 */
export function getVisibleMinyanim(
  roll: any,
  getMinutes: GetMinutes,
  options: { nowMinutes?: number; live?: boolean } = {}
): any[] {
  // Switched off in the roll editor: kept in the roll, never on screen.
  const visible = getRollMinyanim(roll).filter(isMinyanActive);

  const ordered = rollUsesManualOrder(roll)
    ? visible.sort((a, b) => getMinyanOrder(a) - getMinyanOrder(b))
    : visible.sort((a, b) => compareRollDayMinutes(getMinutes(a), getMinutes(b)));

  if (options.live === false) return ordered;
  return applySmartDisplayWindow(
    ordered,
    roll?.design?.smartDisplay,
    options.nowMinutes ?? Number.NaN,
    getMinutes,
    isMinyanSmartDisplayIncluded
  );
}

/** One roll as the display shows it at a tick — see {@link resolveRollDisplay}. */
export interface ResolvedRollDisplay {
  roll: any;
  /** Its minyanim in display order ({@link getVisibleMinyanim}). */
  minyanim: any[];
  /** Whether the roll is live at this tick ({@link isRollLiveNow}). */
  live: boolean;
}

/**
 * One-shot resolver: given an artboard's rolls and a context, return the rolls
 * on screen — highest priority first, each with its visible minyanim and
 * whether it is live — that actually have something to show. Convenient for
 * the live display.
 */
export function resolveRollDisplay(
  rolls: any[],
  ctx: RollContext,
  getMinutes: GetMinutes
): ResolvedRollDisplay[] {
  // A roll whose smart-display window is empty right now drops out here.
  return getActiveRolls(rolls, ctx)
    .map((roll) => {
      const live = isRollLiveNow(roll, ctx);
      return {
        roll,
        live,
        minyanim: getVisibleMinyanim(roll, getMinutes, { nowMinutes: ctx.minutesOfDay, live }),
      };
    })
    .filter((entry) => entry.minyanim.length > 0);
}

export type SecondaryTimeKey = 'bruches' | 'hoidy' | 'ashrei' | 'borchy' | 'netz';

/** The point in a minyan that the roll-wide `{countdown}` variable targets. */
export type RollCountdownTarget = 'minyan' | 'bruches' | 'hoidy' | 'netz';

/** Options shown by the Highlight & Countdown card, in display order. */
export const ROLL_COUNTDOWN_TARGET_OPTIONS: ReadonlyArray<{
  value: RollCountdownTarget;
  label: string;
}> = [
  { value: 'minyan', label: 'Next Minyan' },
  { value: 'bruches', label: 'Bruches' },
  { value: 'hoidy', label: 'Hoidy' },
  { value: 'netz', label: 'Netz' },
];

const ROLL_COUNTDOWN_TARGET_VALUES = new Set<string>(
  ROLL_COUNTDOWN_TARGET_OPTIONS.map((option) => option.value)
);

/**
 * Normalize a saved countdown target. Rolls created before target selection was
 * added (and malformed values) retain the original next-minyan behaviour.
 */
export function normalizeRollCountdownTarget(value: unknown): RollCountdownTarget {
  const normalized = String(value ?? '').trim().toLowerCase();
  return ROLL_COUNTDOWN_TARGET_VALUES.has(normalized)
    ? normalized as RollCountdownTarget
    : 'minyan';
}

/**
 * What a secondary time's offset is measured from:
 *   'minyan'  → the minyan's own start time (a davening milestone).
 *   'sunrise' → the day's real Netz (sunrise) zman, which the display layer
 *               resolves from the screen's location and injects as an anchor.
 */
export type SecondaryTimeAnchor = 'minyan' | 'sunrise';

/** The zman key Netz anchors to (kosher-zmanim's sunrise). */
export const NETZ_ZMAN_KEY = 'sunrise';

export interface MinyanDisplayToggleDef {
  key: SecondaryTimeKey;
  /** The minyan/rule field holding this toggle's offset, e.g. `bruchesOffset`. */
  offsetKey: string;
  /** Hebrew label — shown on the toggle card AND next to the time on screen. */
  label: string;
  /** English label used in the design tab's Variable menus. */
  variableLabel: string;
  /** Offset presets offered as chips (0 renders as "Exact"). */
  presets: number[];
  /** Offset used when nothing valid is stored. */
  default: number;
  /**
   * Smallest offset the editor accepts — 0 lets a toggle sit exactly on its
   * anchor, a negative one lets it come before it.
   */
  minOffset: number;
  anchor: SecondaryTimeAnchor;
  /**
   * Hidden behind the editor's "+N more" reveal instead of shown up-front. Keeps
   * the toggle row to the three everyday milestones while the rest stay one
   * click away. Display behaviour is identical either way.
   */
  collapsed?: boolean;
}

/**
 * THE source of truth for the roll "Display Toggles". Everything that touches a
 * secondary time — the Minyanim tab cards, the Automated-rule cards, the design
 * tab's variable menus, the `{token}` interpolation, the live display, the editor
 * preview and the dashboard — derives from this table, so a new toggle only ever
 * has to be added here (plus its two columns on the backend entity).
 */
export const MINYAN_DISPLAY_TOGGLES: MinyanDisplayToggleDef[] = [
  {
    key: 'bruches',
    offsetKey: 'bruchesOffset',
    label: 'ברכות',
    variableLabel: 'Bruches',
    presets: [5, 10],
    default: BRUCHES_OFFSET_MINUTES,
    // Bruches can start before the minyan's listed time, so a custom offset may
    // be negative (-X = X minutes before) or 0, as far back as a full day. The
    // backend's minyan cast keeps the same floor.
    minOffset: -MINUTES_PER_DAY,
    anchor: 'minyan',
  },
  {
    key: 'hoidy',
    offsetKey: 'hoidyOffset',
    label: 'הודו',
    variableLabel: 'Hoidy',
    presets: [10, 15],
    default: HOIDY_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
  },
  {
    key: 'ashrei',
    offsetKey: 'ashreiOffset',
    label: 'אשרי',
    variableLabel: 'Ashrei',
    presets: [3, 5],
    default: ASHREI_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
  },
  {
    key: 'borchy',
    offsetKey: 'borchyOffset',
    label: 'ברכו',
    variableLabel: 'Borchy',
    presets: [20, 25],
    default: BORCHY_OFFSET_MINUTES,
    minOffset: 1,
    anchor: 'minyan',
    collapsed: true,
  },
  {
    // Netz is not a davening milestone but the sunrise itself, so it anchors to
    // the real zman for the day rather than to the minyan's start time. The
    // offset shifts it off sunrise, and defaults to Exact (0).
    key: 'netz',
    offsetKey: 'netzOffset',
    label: 'נץ החמה',
    variableLabel: 'Netz',
    presets: [0, 1, 2],
    default: NETZ_OFFSET_MINUTES,
    minOffset: 0,
    anchor: 'sunrise',
    collapsed: true,
  },
];

/** Toggles shown up-front on a toggle row. */
export const MINYAN_DISPLAY_TOGGLES_PRIMARY = MINYAN_DISPLAY_TOGGLES.filter((def) => !def.collapsed);
/** Toggles revealed by the row's "+N more" button. */
export const MINYAN_DISPLAY_TOGGLES_EXTRA = MINYAN_DISPLAY_TOGGLES.filter((def) => def.collapsed);

/** Look up a toggle definition by key (undefined for an unknown key). */
export function getMinyanDisplayToggleDef(key: unknown): MinyanDisplayToggleDef | undefined {
  return MINYAN_DISPLAY_TOGGLES.find((def) => def.key === key);
}

/** The chip/menu text for an offset — "Exact" at 0, otherwise signed: "+5" / "-5". */
export function formatToggleOffsetLabel(offsetMinutes: unknown): string {
  const n = Number(offsetMinutes);
  if (!Number.isFinite(n) || n === 0) return 'Exact';
  return n > 0 ? `+${n}` : String(n);
}

/**
 * An offset as a number. Only numbers and non-blank strings count: blank, null
 * and the like are "unset", never 0 — 0 is a real Bruches offset, so reading a
 * missing one as 0 would move it onto the minyan's start instead of its default.
 */
function toToggleOffsetNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return Number.NaN;
}

/** A stored offset, falling back to the toggle's default when it is unusable. */
export function resolveMinyanToggleOffset(
  value: unknown,
  def: Pick<MinyanDisplayToggleDef, 'default' | 'minOffset'>
): number {
  const n = toToggleOffsetNumber(value);
  return Number.isFinite(n) && n >= def.minOffset ? n : def.default;
}

/**
 * Whether an offset typed into a toggle card's Custom box can be saved: whole
 * minutes (the backend column is an integer) no smaller than the toggle's
 * `minOffset`. A half-typed value — "" or a lone "-" — is not.
 */
export function isValidMinyanToggleOffset(
  value: unknown,
  def: Pick<MinyanDisplayToggleDef, 'minOffset'>
): boolean {
  const n = toToggleOffsetNumber(value);
  return Number.isInteger(n) && n >= def.minOffset;
}

/**
 * Every toggle field for a minyan / automated rule, normalized. Used both when
 * seeding a new row (pass nothing → all off, default offsets) and when reading
 * or writing a saved one, so the shape is identical everywhere.
 */
export function normalizeMinyanDisplayToggles(source?: any): Record<string, boolean | number> {
  const out: Record<string, boolean | number> = {};
  for (const def of MINYAN_DISPLAY_TOGGLES) {
    out[def.key] = Boolean(source?.[def.key]);
    out[def.offsetKey] = resolveMinyanToggleOffset(source?.[def.offsetKey], def);
  }
  return out;
}

export interface SecondaryTime {
  key: SecondaryTimeKey;
  label: string;
  offsetMinutes: number;
  anchor: SecondaryTimeAnchor;
}

/** The anchors a secondary time can be measured from, in minutes-of-day. */
export interface SecondaryTimeAnchors {
  /** The minyan's own start time. */
  minyanMinutes?: number | null;
  /** The day's Netz (sunrise) for the screen's location. */
  sunriseMinutes?: number | null;
}

export interface ResolvedSecondaryTime {
  key: SecondaryTimeKey;
  label: string;
  /** Minutes-of-day of the resolved time. */
  minutes: number;
  /** The same value as a 12-hour clock string, e.g. "7:05". */
  time: string;
}

/**
 * Which secondary timestamps a minyan should display, based on its toggles.
 * Each entry carries the offset AND what that offset is measured from — the
 * minyan's start time for the davening milestones, the day's sunrise for Netz.
 */
export function getMinyanSecondaryTimes(minyan: any): SecondaryTime[] {
  return MINYAN_DISPLAY_TOGGLES
    .filter((def) => Boolean(minyan?.[def.key]))
    .map((def) => ({
      key: def.key,
      label: def.label,
      offsetMinutes: resolveMinyanToggleOffset(minyan?.[def.offsetKey], def),
      anchor: def.anchor,
    }));
}

/** One secondary time as minutes-of-day, or null when its anchor is unavailable. */
export function resolveSecondaryTimeMinutes(
  secondary: SecondaryTime,
  anchors: SecondaryTimeAnchors
): number | null {
  const base = secondary.anchor === 'sunrise' ? anchors?.sunriseMinutes : anchors?.minyanMinutes;
  if (!Number.isFinite(base as number)) return null;
  return (base as number) + secondary.offsetMinutes;
}

/**
 * Put a recurring clock time in [0, 1440) without dropping the fractional
 * minute carried by a Show Seconds minyan. Blank/null anchors stay invalid.
 */
function normalizeRollCountdownMinutes(value: unknown): number {
  if (value === null || value === undefined || value === '') return Number.NaN;
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return Number.NaN;
  return ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * Resolve one minyan's configured countdown point as minutes-of-day.
 *
 * Unlike {@link resolveMinyanSecondaryTimes}, this intentionally does not read
 * the secondary time's visibility toggle. Countdown To is a roll-wide setting:
 * its milestone remains usable even when that timestamp is not rendered beside
 * the minyan. Stored per-minyan offsets still apply, with the existing defaults
 * used for missing/invalid values.
 *
 * Netz needs the day's sunrise anchor. If it is temporarily unavailable (for
 * example while location zmanim load), fall back to the primary minyan time so
 * the countdown remains useful instead of disappearing. Invalid/legacy targets
 * are normalized to that same primary-minyan behaviour.
 */
export function resolveRollCountdownTargetMinutes(
  minyan: any,
  target: unknown,
  anchors: SecondaryTimeAnchors
): number {
  const primaryMinutes = normalizeRollCountdownMinutes(anchors?.minyanMinutes);
  const normalizedTarget = normalizeRollCountdownTarget(target);
  if (normalizedTarget === 'minyan') return primaryMinutes;

  const def = getMinyanDisplayToggleDef(normalizedTarget);
  if (!def) return primaryMinutes;

  const anchor = def.anchor === 'sunrise'
    ? anchors?.sunriseMinutes
    : anchors?.minyanMinutes;
  const anchorMinutes = normalizeRollCountdownMinutes(anchor);
  if (!Number.isFinite(anchorMinutes)) return primaryMinutes;

  const offset = resolveMinyanToggleOffset(minyan?.[def.offsetKey], def);
  return normalizeRollCountdownMinutes(anchorMinutes + offset);
}

/**
 * A minyan's enabled secondary times, resolved to real clock times against the
 * supplied anchors. Entries whose anchor can't be resolved (e.g. Netz before the
 * screen's zmanim have loaded) are dropped rather than shown as "--:--", so the
 * live display, the editor preview and the dashboard all render the same list.
 */
export function resolveMinyanSecondaryTimes(
  minyan: any,
  anchors: SecondaryTimeAnchors
): ResolvedSecondaryTime[] {
  const out: ResolvedSecondaryTime[] = [];
  // A Show Seconds minyan's start carries its seconds, so the milestones measured
  // from it (Bruches +5 …) show them too. Netz hangs off sunrise, not the minyan.
  const minyanShowsSeconds = isMinyanShowSecondsEnabled(minyan);
  for (const secondary of getMinyanSecondaryTimes(minyan)) {
    const minutes = resolveSecondaryTimeMinutes(secondary, anchors);
    if (minutes === null) continue;
    out.push({
      key: secondary.key,
      label: secondary.label,
      minutes,
      time: formatClockFromMinutes(minutes, {
        withSeconds: minyanShowsSeconds && secondary.anchor === 'minyan',
      }),
    });
  }
  return out;
}

/* -------------------------------------------------------------------------
 * Roll design variables ({token} placeholders)
 * ---------------------------------------------------------------------- */

/** The non-toggle variables every roll design offers. */
const ROLL_BASE_VARIABLES: Array<{ value: string; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'room', label: 'Room' },
  { value: 'time', label: 'Time' },
  { value: 'countdown', label: 'Countdown' },
];

/**
 * The Variable menu shown in the design tab (Grid Designer, Upcoming editor) and
 * the List-view field pickers — the base fields plus one entry per display
 * toggle, in the same order the toggle cards appear.
 */
export const ROLL_DESIGN_VARIABLES: Array<{ value: string; label: string }> = [
  ...ROLL_BASE_VARIABLES,
  ...MINYAN_DISPLAY_TOGGLES.map((def) => ({ value: def.key, label: def.variableLabel })),
];

/**
 * Every `{token}` a roll design cell can contain (`minyan` aliases `name`), plus
 * the roll-wide `parsha` — exactly what the Date/Time banner shows for {parsha}
 * (the holiday header during holidays, otherwise the week's parsha) — which the
 * Add/Edit Text modals offer.
 */
const ROLL_FIELD_TOKENS = [
  'name',
  'minyan',
  ...ROLL_DESIGN_VARIABLES.map((v) => v.value).filter((v) => v !== 'name'),
  'parsha',
];
const ROLL_FIELD_TOKEN_PATTERN = `\\{(${ROLL_FIELD_TOKENS.join('|')})\\}`;

/** Matches a single `{token}` — for "does this cell have content?" checks. */
export const ROLL_FIELD_TOKEN_REGEX = new RegExp(ROLL_FIELD_TOKEN_PATTERN);
/** Global variant for interpolation. Safe to share: `String.replace` resets lastIndex. */
export const ROLL_FIELD_TOKEN_REGEX_GLOBAL = new RegExp(ROLL_FIELD_TOKEN_PATTERN, 'g');

/** Gap (px) between cards in a roll's card-view segment; the 2-column centering uses it. */
export const ROLL_CARD_GAP_PX = 12;

/**
 * Design tab → Columns (`design.displayView.columns`), shared by card and list
 * view: presets 1–3 plus a Custom count of 4–12 (utils/column_layout). Only the
 * plain number is stored, and the editor preview and the display both read it
 * through here: a whole number 1–12, missing/invalid → 1.
 */
export function normalizeRollColumns(value: unknown): number {
  return clampColumnCount(value);
}

/** Grid tracks for a roll card-view segment: 2 × columns from 3 up (see utils/column_layout). */
export function getRollCardTemplateColumns(columns: unknown): number {
  return getCenteredCardTemplateColumns(columns);
}

/** Per-card placement that centers a short last row of roll cards (see utils/column_layout). */
export function getRollCardGridPlacement(columns: unknown, index: number, total: number): Record<string, string> {
  return getCenteredCardPlacement(columns, index, total, ROLL_CARD_GAP_PX);
}

/**
 * Format minutes-of-day as a 12-hour clock string, e.g. 425 → "7:05".
 *
 * `withSeconds` reads the fraction as seconds, e.g. 425.5 → "7:05:30" — the
 * clock of a Show Seconds minyan ({@link isMinyanShowSecondsEnabled}). Without
 * it the value is rounded to the whole minute.
 */
export function formatClockFromMinutes(
  totalMinutes: number,
  options: { withPeriod?: boolean; withSeconds?: boolean } = {},
): string {
  if (!Number.isFinite(totalMinutes)) return '--:--';
  if (options.withSeconds) {
    // Rounded to the whole second first, so the float noise of minutes + s/60
    // (402.1666…) can never show :09 for :10.
    const secondsPerDay = MINUTES_PER_DAY * 60;
    const totalSeconds = ((Math.round(totalMinutes * 60) % secondsPerDay) + secondsPerDay) % secondsPerDay;
    const hours24 = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const base = `${hours24 % 12 || 12}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    if (!options.withPeriod) return base;
    return `${base} ${hours24 >= 12 ? 'PM' : 'AM'}`;
  }
  const normalized = ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours24 = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const hours12 = hours24 % 12 || 12;
  const base = `${hours12}:${String(minutes).padStart(2, '0')}`;
  if (!options.withPeriod) return base;
  return `${base} ${hours24 >= 12 ? 'PM' : 'AM'}`;
}

/** Milliseconds until the next occurrence of a minutes-of-day target. */
/**
 * Milliseconds until the clock next reads `targetMinutes`, on a clock whose
 * current minute-of-day is `nowMinutes` (the screen's timezone) — seconds and
 * millis are read from `now` itself, since IANA offsets are whole minutes.
 * Already passed today → next occurrence tomorrow.
 */
export function getRollCountdownDiffMsOnClock(now: Date, nowMinutes: number, targetMinutes: number): number {
  if (!now || !Number.isFinite(nowMinutes) || !Number.isFinite(targetMinutes)) return Number.NaN;
  const current = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(current.getTime())) return Number.NaN;
  const nowMsOfDay =
    Math.round(nowMinutes) * 60000 + current.getSeconds() * 1000 + current.getMilliseconds();
  // Rounded as milliseconds, not minutes: a Show Seconds minyan's fractional
  // minute (402.1666… = 6:42:10) counts down to its exact second.
  let diff = Math.round(targetMinutes * 60000) - nowMsOfDay;
  if (diff <= 0) diff += MINUTES_PER_DAY * 60000;
  return diff;
}

export function getRollCountdownDiffMs(now: Date, targetMinutes: number): number {
  if (!now || !Number.isFinite(targetMinutes)) return Number.NaN;
  const current = now instanceof Date ? now : new Date(now);
  const currentMs = current.getTime();
  if (!Number.isFinite(currentMs)) return Number.NaN;

  const target = new Date(current);
  target.setHours(0, 0, 0, 0);
  target.setMinutes(Math.round(targetMinutes), 0, 0);

  if (target.getTime() <= currentMs) {
    target.setDate(target.getDate() + 1);
  }

  return target.getTime() - currentMs;
}

/** Format a countdown as HH:MM:SS. Hours are not capped. */
export function formatRollCountdown(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/* =========================================================================
 * Automated Minyan Generator
 * -------------------------------------------------------------------------
 * Expands a single Automated Rule into concrete minyan items, implementing
 * the spec:
 *   1. Schedule Anchor (Window)  — start/end bounds + interval gap.
 *   2. Time-Snap & Rounding      — snap_grid = currentTime.roundTo(grid),
 *                                  with the "Exact Start" exception.
 *   4. Fixed Pattern Sync        — room_index =
 *                                  floor(total_minutes_since_midnight / interval) % room_pool.length
 *      (when disabled, rooms rotate sequentially in priority order).
 *
 * Zmanim resolution lives in the display layer, so the caller injects
 * `resolveBoundaryMinutes(boundary)` which turns a saved AutomatedTimeBoundary
 * into minutes-of-day (or null when it cannot be resolved).
 * ====================================================================== */

export interface AutomatedRoom {
  id: string;
  name: string;
}

export interface GeneratedMinyan {
  id: string;
  /** Display label (room name when a room pool is set, else the minyan name). */
  name: string;
  minyanName: string;
  roomName: string | null;
  roomId: string | null;
  roomLocation?: string;
  timingMode: 'fixed';
  fixedTime: string; // 24h "HH:mm"
  /** The generating rule's own smart-display settings, so its minyanim are
   *  windowed on their own terms rather than by the roll's (see
   *  {@link applySmartDisplayWindow}). Undefined when the rule has none. */
  smartDisplayRule?: any;
  /** Display toggles inherited from the rule (one `<key>` + `<key>Offset` pair
   *  per entry in {@link MINYAN_DISPLAY_TOGGLES}), in the same shape as a
   *  manually-defined minyan so the display layer treats both identically
   *  (see {@link getMinyanSecondaryTimes}). */
  [toggleField: string]: any;
  __generated: true;
  __ruleId: string;
}

export interface AutomatedGenerateOptions {
  /** Resolve a saved AutomatedTimeBoundary to minutes-of-day [0,1440) or null. */
  resolveBoundaryMinutes: (boundary: any) => number | null;
  /** Hard cap on generated minyanim per rule (safety). Defaults to 200. */
  maxCount?: number;
}

/**
 * Round a minutes value to the nearest multiple of `grid` (the shared "Round
 * Start Time To" snap used by Automated rules and by Shabbos dynamic minyanim).
 * A non-positive grid (Exact) leaves the value untouched.
 */
export function roundMinutesToNearest(totalMinutes: number, grid: number): number {
  if (!Number.isFinite(totalMinutes) || !grid || grid <= 0) return totalMinutes;
  return Math.round(totalMinutes / grid) * grid;
}

/**
 * A minyan's "Round Start Time To" grid in minutes (0 = Exact / no rounding).
 * Meaningful for Shabbos-roll dynamic-zman minyanim and for Weekly Static Zman
 * minyanim (any roll type), where the resolved zman time is snapped to this
 * grid on the display, the editor preview, sorting, secondary times and the
 * countdown.
 *
 * 0 while Show Seconds is on: the exact time is shown, so no grid applies. The
 * stored grid is kept and takes effect again once the toggle is turned off.
 */
export function getMinyanRoundToMinutes(minyan: any): number {
  if (isMinyanShowSecondsEnabled(minyan)) return 0;
  const grid = Number(minyan?.roundToMinutes);
  return Number.isFinite(grid) && grid > 0 ? grid : 0;
}

/* -------------------------------------------------------------------------
 * Weekly Static Zman timing mode
 * -------------------------------------------------------------------------
 * A minyan on this mode resolves its zman for EVERY day of its week — Sunday
 * through Friday, from Sunday midnight to Friday midnight (see
 * {@link getWeeklyZmanWeekDates}) — then shows the minimum or maximum of those
 * six clock times: one static time for the whole week. That time is then
 * shifted by the minyan's Offset (± minutes) and only afterwards snapped by an
 * optional "Round Time To" grid in the minyan's "Round To" direction (closer /
 * high / lower) — see {@link resolveWeeklyZmanMinutes}.
 */

/** Canonical stored value for the Weekly Static Zman timing mode. */
export const WEEKLY_ZMAN_TIMING_MODE = 'weeklyZman';

/** True when a stored timingMode means "Weekly Static Zman" (any variant). */
export function isWeeklyZmanTimingMode(mode: unknown): boolean {
  const raw = String(mode ?? '').trim().toLowerCase();
  return raw === 'weeklyzman' || raw === 'weekly_zman' || raw === 'weekly-zman' || raw === 'weekly';
}

/** The minyan's Max/Min selection for Weekly Static Zman ('max' by default). */
export function getMinyanWeeklyZmanMode(minyan: any): 'max' | 'min' {
  return minyan?.weeklyZmanMode === 'min' ? 'min' : 'max';
}

// Sunday through Friday.
const WEEKLY_ZMAN_WEEK_DAYS = 6;

/**
 * The dates a Weekly Static Zman week scans, for the org-local calendar date
 * `civilDate` (a Date at local noon, as the display's getScreenCivilDate builds
 * it): Sunday through Friday of the week that date is in — from Sunday midnight
 * to Friday midnight. Shabbos is not scanned; it belongs to the week that began
 * the Sunday before it, so a week's time holds until the next Sunday midnight.
 * Each date keeps `civilDate`'s noon, so a zmanim calendar set to it reads the
 * intended day whatever the DST offset. The live display and the editor
 * preview both scan exactly these dates.
 */
export function getWeeklyZmanWeekDates(civilDate: Date): Date[] {
  const sunday = new Date(civilDate.getTime());
  sunday.setDate(civilDate.getDate() - civilDate.getDay());
  return Array.from({ length: WEEKLY_ZMAN_WEEK_DAYS }, (_, index) => {
    const date = new Date(sunday.getTime());
    date.setDate(sunday.getDate() + index);
    return date;
  });
}

/* -------------------------------------------------------------------------
 * Show Seconds (Dynamic Zman / Weekly Static Zman)
 * -------------------------------------------------------------------------
 * The per-minyan counterpart of the Zmanim boards' Time Settings → Show
 * Seconds. While on, the minyan shows its zman's exact clock time, seconds
 * included (6:42:10), instead of folding the seconds into a whole minute: the
 * organization's Zmanim "Round To" and the minyan's own rounding grid are both
 * skipped.
 *
 * Such a minyan's minutes-of-day value carries the seconds as a fraction
 * (6:42:10 → 402.1666…). That is the one value sort, highlight, started colour,
 * secondary times and the countdown already share, so all of them land on the
 * exact second without a parallel field. Whole-minute values are unaffected.
 */

/** True when a minyan shows seconds: the toggle is on AND its timing is a zman. */
export function isMinyanShowSecondsEnabled(minyan: any): boolean {
  if (minyan?.showSeconds !== true) return false;
  if (minyan?.itemType === 'text' || minyan?.kind === 'text' || minyan?.type === 'text') return false;
  return minyan?.timingMode === 'zmanim' || isWeeklyZmanTimingMode(minyan?.timingMode);
}

/**
 * A zman instant's minutes-of-day with its whole seconds added as a fraction
 * (milliseconds dropped — the same truncation its h:mm:ss clock shows). Seconds
 * read the same in every timezone, as every IANA offset is whole minutes, so
 * `minutesOfDay` may come from any org-timezone conversion. Non-finite minutes
 * and unparseable instants come back untouched.
 */
export function withZmanSeconds(minutesOfDay: number, iso: any): number {
  if (!Number.isFinite(minutesOfDay) || !iso) return minutesOfDay;
  const date = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(date.getTime())) return minutesOfDay;
  return minutesOfDay + date.getUTCSeconds() / 60;
}

/**
 * "Round To" direction for the grid snap, mirroring the Zmanim override values:
 *   closer → nearest grid mark (midpoint rounds up)   [default]
 *   high   → always up to the next grid mark
 *   lower  → always down to the previous grid mark
 */
export const MINYAN_ROUND_DIRECTIONS = {
  closer: 'round to closer',
  high: 'round to high',
  lower: 'round to lower',
} as const;

/** A minyan's stored "Round To" direction, defaulting to `closer`. */
export function getMinyanRoundDirection(minyan: any): string {
  const dir = minyan?.roundDirection;
  return dir === MINYAN_ROUND_DIRECTIONS.high || dir === MINYAN_ROUND_DIRECTIONS.lower
    ? dir
    : MINYAN_ROUND_DIRECTIONS.closer;
}

/**
 * Snap a minutes value to `grid` in the requested direction. A non-positive
 * grid (Exact) leaves the value untouched; an unknown/empty direction rounds to
 * the nearest mark (same as {@link roundMinutesToNearest}).
 */
export function roundMinutesToGridDirectional(
  totalMinutes: number,
  grid: number,
  direction?: string,
): number {
  if (!Number.isFinite(totalMinutes) || !grid || grid <= 0) return totalMinutes;
  if (direction === MINYAN_ROUND_DIRECTIONS.lower) return Math.floor(totalMinutes / grid) * grid;
  if (direction === MINYAN_ROUND_DIRECTIONS.high) return Math.ceil(totalMinutes / grid) * grid;
  return roundMinutesToNearest(totalMinutes, grid);
}

/**
 * Apply a minyan's own "Round Time To" grid + "Round To" direction to a resolved
 * minutes-of-day value. The single place that pairs the two settings, so the
 * editor preview, the live display and the dashboard preview all snap a Weekly
 * Static Zman time identically. Grid 0 (Exact) returns the value untouched.
 *
 * e.g. 2:17 → :05 lower = 2:15, high = 2:20; :10 lower = 2:10, high = 2:20.
 *
 * Rounding only — Weekly Static Zman consumers must call
 * {@link resolveWeeklyZmanMinutes} instead, which applies the minyan's Offset
 * first and then rounds.
 */
export function roundMinyanZmanMinutes(minyan: any, totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  const grid = getMinyanRoundToMinutes(minyan);
  if (grid <= 0) return totalMinutes;
  return roundMinutesToGridDirectional(totalMinutes, grid, getMinyanRoundDirection(minyan));
}

/** Wrap a minutes value into [0, 1440) so a ± offset that crosses midnight lands
 *  on the real clock time (and sorts there) instead of running out of range. */
function normalizeMinutesOfDay(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  return ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/**
 * A minyan's zman Offset in minutes: positive shifts the resolved time later,
 * negative earlier, 0 when unset. Shared by Dynamic Zman and Weekly Static Zman
 * (both store it on `offsetMinutes`), so the two modes read it identically.
 */
export function getMinyanOffsetMinutes(minyan: any): number {
  const raw = (
    minyan?.offsetMinutes ??
    minyan?.zmanimOffsetMinutes ??
    minyan?.zmanim_offset_minutes ??
    minyan?.timing?.zmanim?.offsetMinutes ??
    minyan?.timing?.zman?.offsetMinutes ??
    0
  );
  const offset = Number(raw);
  return Number.isFinite(offset) ? offset : 0;
}

/**
 * The full Weekly Static Zman pipeline for a resolved weekly Min/Max
 * minutes-of-day: apply the minyan's Offset FIRST, then snap the result to its
 * "Round Time To" grid in its "Round To" direction. The one place that fixes
 * that order, so the editor preview, the live display and the dashboard preview
 * all land on the same clock, sort key, secondary times and countdown.
 *
 * e.g. weekly Max 6:57 with Offset +5 → 7:02, then :10 lower = 7:00 / high = 7:10.
 *
 * With Show Seconds on, the seconds fraction survives the Offset and no grid
 * snap follows: weekly Max 6:57:40 with Offset +5 → 7:02:40.
 */
export function resolveWeeklyZmanMinutes(minyan: any, totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes)) return totalMinutes;
  const shifted = totalMinutes + getMinyanOffsetMinutes(minyan);
  if (isMinyanShowSecondsEnabled(minyan)) {
    // normalizeMinutesOfDay rounds to the minute, so wrap without it.
    return ((shifted % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  }
  return roundMinyanZmanMinutes(minyan, normalizeMinutesOfDay(shifted));
}

/** An Offset as a summary suffix: 5 → "+5 min", -5 → "-5 min", 0 → "". */
function formatMinyanOffsetLabel(offsetMinutes: unknown): string {
  const offset = Number(offsetMinutes);
  if (!Number.isFinite(offset) || offset === 0) return '';
  return `${offset > 0 ? '+' : ''}${offset} min`;
}

/**
 * Editor/summary label for a Weekly Static Zman minyan whose real clock time
 * isn't resolvable yet (no location/zmanim data), e.g.
 * "Alos 72 (Weekly Max +5 min)".
 */
export function getWeeklyZmanSummaryLabel(zmanLabel: string, minyan: any): string {
  const mode = getMinyanWeeklyZmanMode(minyan) === 'min' ? 'Min' : 'Max';
  const offset = formatMinyanOffsetLabel(getMinyanOffsetMinutes(minyan));
  return `${zmanLabel} (Weekly ${mode}${offset ? ` ${offset}` : ''})`;
}

/** snap_grid: currentTime.roundTo(gridValue) — snaps to the nearest grid mark. */
function roundMinutesToGrid(totalMinutes: number, grid: number): number {
  return roundMinutesToNearest(totalMinutes, grid);
}

/** Format minutes-of-day as a 24h "HH:mm" string (wraps across midnight). */
function clock24FromMinutes(totalMinutes: number): string {
  const normalized = ((Math.round(totalMinutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Normalize a stored room pool (ids or {id,name} objects) to {id,name}. */
function normalizeRoomPool(rooms: any): AutomatedRoom[] {
  if (!Array.isArray(rooms)) return [];
  return rooms
    .map((room): AutomatedRoom | null => {
      if (room && typeof room === 'object') {
        const id = String(room.id ?? room._id ?? room.name ?? '');
        const name = String(room.name ?? room.id ?? room._id ?? '');
        if (!id && !name) return null;
        return { id, name };
      }
      const value = String(room ?? '').trim();
      if (!value) return null;
      return { id: value, name: value };
    })
    .filter((room): room is AutomatedRoom => room !== null);
}

/** The minyan name for a rule (handles the `custom` name type). */
export function getAutomatedRuleMinyanName(rule: any): string {
  if (!rule) return '';
  if (rule.nameType === 'custom') return rule.customName || rule.name || 'Custom';
  return rule.nameType || rule.name || '';
}

/** The rounding grid (minutes) for a rule, or 0 when no rounding applies. */
function getRuleGrid(rule: any): number {
  const grid = Number(rule?.roundToMinutes);
  return Number.isFinite(grid) && grid > 0 ? grid : 0;
}

/**
 * Expand one Automated Rule into concrete, fixed-time minyan items.
 * Returns an empty array when the rule is disabled or its window cannot be
 * resolved (e.g. a zman that is not available for the current day).
 */
export function generateAutomatedMinyanim(rule: any, options: AutomatedGenerateOptions): GeneratedMinyan[] {
  if (!rule || rule.isEnabled === false) return [];

  const interval = Number(rule.intervalMinutes ?? rule.interval);
  if (!Number.isFinite(interval) || interval <= 0) return [];

  const startMinutes = options.resolveBoundaryMinutes(rule.startTime);
  if (startMinutes === null || !Number.isFinite(startMinutes)) return [];

  let endMinutes = options.resolveBoundaryMinutes(rule.endTime);
  if (endMinutes === null || !Number.isFinite(endMinutes)) return [];
  if (endMinutes < startMinutes) endMinutes += MINUTES_PER_DAY; // window crosses midnight

  const grid = getRuleGrid(rule);
  const exactStart = Boolean(rule.exactStart);
  const fixedPatternSync = Boolean(rule.fixedPatternSync);
  const pool = normalizeRoomPool(rule.rooms);
  const minyanName = getAutomatedRuleMinyanName(rule);
  const maxCount = Number.isFinite(options.maxCount as number) && (options.maxCount as number) > 0
    ? Math.floor(options.maxCount as number)
    : 200;

  // Display toggles are configured once on the rule and inherited by every
  // generated minyan, exactly like a manually-defined minyan's toggles.
  const displayToggles = normalizeMinyanDisplayToggles(rule);
  // Smart display is configured per rule, so every minyan it generates carries
  // that rule's settings rather than falling under the roll's.
  const smartDisplayRule = rule.smartDisplay ? normalizeSmartDisplay(rule.smartDisplay) : undefined;

  const out: GeneratedMinyan[] = [];
  let lastSnapped: number | null = null;

  for (let i = 0; i < 1000; i++) {
    const raw = startMinutes + i * interval;
    if (raw > endMinutes) break;

    // "Exact Start" exception: when enabled the very first minyan keeps the
    // anchor; every later minyan snaps to the grid. When disabled, even the
    // first snaps.
    let snapped: number;
    if (grid > 0 && !(i === 0 && exactStart)) {
      snapped = roundMinutesToGrid(raw, grid);
    } else {
      snapped = raw;
    }

    if (snapped > endMinutes) break;
    if (snapped === lastSnapped) continue; // skip duplicate slot after snapping

    let roomName: string | null = null;
    let roomId: string | null = null;
    if (pool.length > 0) {
      let index: number;
      if (fixedPatternSync) {
        // Ghost Grid: room_index = (total_minutes_since_midnight / interval) % pool.length
        const minutesSinceMidnight = ((Math.round(snapped) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
        index = Math.floor(minutesSinceMidnight / interval) % pool.length;
      } else {
        index = out.length % pool.length; // sequential rotation in priority order
      }
      index = ((index % pool.length) + pool.length) % pool.length;
      roomName = pool[index].name;
      roomId = pool[index].id;
    }

    out.push({
      id: `${rule.id || 'auto'}-${i}`,
      name: roomName || minyanName,
      minyanName,
      roomName,
      roomId,
      roomLocation: roomName || undefined,
      timingMode: 'fixed',
      fixedTime: clock24FromMinutes(snapped),
      ...displayToggles,
      ...(smartDisplayRule ? { smartDisplayRule } : {}),
      __generated: true,
      __ruleId: String(rule.id || ''),
    });
    lastSnapped = snapped;

    if (out.length >= maxCount) break;
  }

  return out;
}

/** Expand all enabled automated rules on a roll into generated minyan items. */
export function generateRollAutomatedMinyanim(roll: any, options: AutomatedGenerateOptions): GeneratedMinyan[] {
  const rules = Array.isArray(roll?.automatedRules) ? roll.automatedRules : [];
  const out: GeneratedMinyan[] = [];
  for (const rule of rules) {
    out.push(...generateAutomatedMinyanim(rule, options));
  }
  return out;
}
